/**
 * expressions.js — $ref Expression Resolver
 *
 * Resolves dynamic values in Composed Mechanic JSON.
 * Static values pass through unchanged; $ref objects resolve via
 * dot-notation path traversal on a context object.
 *
 * Part of the Composable Mechanic Engine (Issue #82, Phase A: #174)
 *
 * @example
 *   resolveExpression(3, ctx)                                    // → 3
 *   resolveExpression({ $ref: 'character.traits.Knowledge' }, ctx) // → 2
 *   resolveExpression({ $ref: 'character.missing', $default: 5 }, ctx) // → 5
 */

/**
 * Check if a value is a $ref expression object.
 * @param {*} value
 * @returns {boolean}
 */
export const isExpression = (value) =>
  value !== null && value !== undefined && typeof value === 'object' && !Array.isArray(value) && '$ref' in value;

/**
 * Resolve a value that may be static or a $ref expression.
 *
 * @param {number|string|boolean|object} value - Static value or { $ref, $default? }
 * @param {object} [context={}] - Resolution context: { character, cardKey, sessionState }
 * @returns {*} Resolved value (never throws)
 */
export const resolveExpression = (value, context = {}) => {
  // Static values pass through
  if (!isExpression(value)) return value;

  const { $ref, $default = 0 } = value;
  if (typeof $ref !== 'string' || $ref.length === 0) return $default;

  // Replace {cardKey} placeholder with actual cardKey from context
  const resolvedRef = $ref.replace(/\{cardKey\}/g, context.cardKey || '');

  // Split into root object name + remaining path
  // e.g. "character.traits.Knowledge" → root="character", path=["traits","Knowledge"]
  const [root, ...pathParts] = resolvedRef.split('.');
  const rootObj = context[root];

  if (rootObj === undefined) return $default;
  if (pathParts.length === 0) return rootObj ?? $default;

  const resolved = pathParts.reduce(
    (current, key) => (current === null || current === undefined ? undefined : current[key]),
    rootObj
  );

  return resolved ?? $default;
};
